import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { resetPassword } from '../api/auth-api';

const PASSWORD_MIN_LENGTH = 12;

function validatePassword(pw: string): string | undefined {
  if (!pw) return 'Password is required.';
  if (pw.length < PASSWORD_MIN_LENGTH) return `Must be at least ${PASSWORD_MIN_LENGTH} characters.`;
  if (!/[A-Z]/.test(pw)) return 'Must contain an uppercase letter.';
  if (!/[a-z]/.test(pw)) return 'Must contain a lowercase letter.';
  if (!/[0-9]/.test(pw)) return 'Must contain a number.';
  if (!/[^A-Za-z0-9]/.test(pw)) return 'Must contain a special character.';
  return undefined;
}

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [succeeded, setSucceeded] = useState(false);

  if (!token) {
    return (
      <div className="w-full max-w-md">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 p-8 text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-3">Missing reset link</h1>
          <p className="text-gray-500 dark:text-gray-400 mb-6">
            This page expects a reset token in the URL. Please use the most recent link from your email.
          </p>
          <Link to="/forgot-password" className="text-sm text-primary-600 dark:text-primary-400 hover:underline">
            Request a new reset link
          </Link>
        </div>
      </div>
    );
  }

  function validate(): boolean {
    const next: Record<string, string> = {};
    const pwErr = validatePassword(password);
    if (pwErr) next.password = pwErr;
    if (!confirm) next.confirm = 'Please confirm your password.';
    else if (password !== confirm) next.confirm = 'Passwords do not match.';
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    setSubmitError('');
    setSubmitting(true);
    try {
      const response = await resetPassword(token, password);
      if (response.success) {
        setSucceeded(true);
      } else {
        setSubmitError(response.message || 'This reset link is invalid or has expired.');
      }
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setSubmitError(message || 'This reset link is invalid or has expired.');
    } finally {
      setSubmitting(false);
    }
  }

  if (succeeded) {
    return (
      <div className="w-full max-w-md">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-success-50 dark:bg-success-500/20 flex items-center justify-center mx-auto mb-5">
            <CheckCircle2 className="w-10 h-10 text-success-500" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-3">Password reset</h1>
          <p className="text-gray-500 dark:text-gray-400 mb-6">
            You can now sign in with your new password. Any existing sessions on other devices have been signed out.
          </p>
          <Button onClick={() => navigate('/login')} className="w-full">
            Continue to sign in
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 p-8">
        <h2 className="text-3xl font-bold text-center mb-2 text-gray-900 dark:text-gray-100">Choose a new password</h2>
        <p className="text-center text-gray-600 dark:text-gray-400 mb-8">
          This link expires 30 minutes after it was emailed.
        </p>

        <form noValidate onSubmit={handleSubmit} className="space-y-5">
          <Input
            label="New password"
            type="password"
            required
            value={password}
            onChange={(e) => { setPassword(e.target.value); setErrors(p => ({ ...p, password: '' })); }}
            helperText={`At least ${PASSWORD_MIN_LENGTH} characters with uppercase, lowercase, number, and special character.`}
            error={errors.password}
            autoFocus
          />

          <Input
            label="Confirm password"
            type="password"
            required
            value={confirm}
            onChange={(e) => { setConfirm(e.target.value); setErrors(p => ({ ...p, confirm: '' })); }}
            error={errors.confirm}
          />

          {submitError && (
            <div className="p-3 rounded-lg bg-error-50 dark:bg-error-500/10 border border-error-200 dark:border-error-500/30">
              <p className="text-sm text-error-700 dark:text-error-400">{submitError}</p>
            </div>
          )}

          <Button type="submit" className="w-full py-3 text-base" isLoading={submitting}>
            Reset password
          </Button>
        </form>

        <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-6">
          <Link to="/login" className="text-primary-600 dark:text-primary-400 hover:underline">
            Back to sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
